import { createFileRoute, Link } from "@tanstack/react-router"
import { RefreshCw, SearchX } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"

import { MONTH_NAMES } from "@/components/members/month-picker"
import { EmptyState } from "@/components/shared/empty-state"
import { StatusAlert } from "@/components/shared/status-alert"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { GUILD_RANK_LABEL } from "@/core/guild-members/titles"
import {
  useApplyTitleSuggestionsMutation,
  useTitleSuggestionsQuery,
} from "@/hooks/title-suggestions"
import { formatGuildJoinDate } from "@/lib/guild-join-date"
import {
  type ApplyTitleSuggestionResult,
  type TitleSuggestion,
  TitleSuggestionsApiError,
} from "@/lib/title-suggestions-api"

export const Route = createFileRoute(
  "/_authed/_admin/members/title-suggestions",
)({
  component: TitleSuggestionsPage,
})

const PRESENCE_LABEL = {
  present: "Present",
  departed: "Departed",
  unknown: "Unknown",
} as const

const periodLabel = (year: number, month: number) =>
  `${MONTH_NAMES[month - 1]} ${year}`

function suggestionStatus(row: TitleSuggestion, sourceLabel: string) {
  if (row.eligibility === "eligible") {
    return row.changeRequired
      ? `Suggest ${GUILD_RANK_LABEL[row.suggestedRank]}`
      : `Already matches ${GUILD_RANK_LABEL[row.suggestedRank]}`
  }
  switch (row.reason) {
    case "leadership-rank":
      return `Excluded because the current rank is ${row.currentRank ? GUILD_RANK_LABEL[row.currentRank] : "a leadership rank"}.`
    case "discord-departed":
      return "Discord presence is departed."
    case "discord-unknown":
      return "Discord presence is unknown."
    case "missing-activity-points":
      return `Activity points for ${sourceLabel} are missing.`
    case "missing-guild-join-date":
      return "Guild join date is missing."
  }
}

function resultDescription(result: ApplyTitleSuggestionResult) {
  switch (result.status) {
    case "applied":
      return `Applied ${GUILD_RANK_LABEL[result.rankTitle]}.`
    case "already-current":
      return `Already current at ${GUILD_RANK_LABEL[result.rankTitle]}.`
    case "ineligible":
      return "No longer eligible; no change was made."
    case "rank-conflict":
      return "Rank changed during review; no change was made."
    case "not-found":
      return "Member was not found; no change was made."
    case "failed":
      return "The update failed; the final rank could not be confirmed."
  }
}

function TitleSuggestionsPage() {
  const authenticatedMemberId =
    Route.useRouteContext().auth.user?.userId ?? null
  const query = useTitleSuggestionsQuery()
  const mutation = useApplyTitleSuggestionsMutation(authenticatedMemberId)
  const [selected, setSelected] = useState<Set<string>>(() => new Set())
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [results, setResults] = useState<ApplyTitleSuggestionResult[] | null>(
    null,
  )
  const [requestMessage, setRequestMessage] = useState<string | null>(null)
  const metadataRef = useRef<string | null>(null)

  const selectableIds = useMemo(
    () =>
      new Set(
        query.data?.rows
          .filter((row) => row.eligibility === "eligible" && row.changeRequired)
          .map((row) => row.memberId) ?? [],
      ),
    [query.data],
  )
  const metadata = query.data
    ? `${query.data.sourcePeriod.year}-${query.data.sourcePeriod.month}:${query.data.evaluatedOn}`
    : null

  useEffect(() => {
    if (!query.data) return
    const changedMetadata =
      metadataRef.current !== null && metadataRef.current !== metadata
    metadataRef.current = metadata
    setSelected((current) =>
      changedMetadata
        ? new Set()
        : new Set(
            [...current].filter((memberId) => selectableIds.has(memberId)),
          ),
    )
    if (changedMetadata) setConfirmOpen(false)
  }, [metadata, query.data, selectableIds])

  const busy = query.isFetching || mutation.isPending
  const selectedIds = [...selected].filter((memberId) =>
    selectableIds.has(memberId),
  )
  const allSelected =
    selectableIds.size > 0 && selectedIds.length === selectableIds.size
  const sourceLabel = query.data
    ? periodLabel(query.data.sourcePeriod.year, query.data.sourcePeriod.month)
    : "the source month"

  const confirmApply = async () => {
    if (!query.data || query.isError || busy) return
    const memberIds = selectedIds
    if (memberIds.length === 0) return
    try {
      const response = await mutation.mutateAsync({
        memberIds,
        sourcePeriod: query.data.sourcePeriod,
        evaluatedOn: query.data.evaluatedOn,
      })
      setResults(response.results)
      setRequestMessage(null)
      setSelected((current) => {
        const next = new Set(current)
        for (const result of response.results) {
          if (
            result.status === "applied" ||
            result.status === "already-current"
          )
            next.delete(result.memberId)
        }
        return next
      })
    } catch (error) {
      const stale =
        error instanceof TitleSuggestionsApiError &&
        error.status === 409 &&
        error.code === "STALE_TITLE_SUGGESTIONS"
      setSelected(new Set())
      setRequestMessage(
        stale
          ? "The evaluation date or source month changed. Review the refreshed suggestions before applying."
          : "The apply result could not be confirmed. Review the refreshed suggestions before trying again.",
      )
    } finally {
      setConfirmOpen(false)
    }
  }

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <Link
            to="/members"
            search={{ state: "active" }}
            className="text-sm font-medium text-primary underline-offset-4 hover:underline"
          >
            Back to Members
          </Link>
          <h1 className="text-2xl font-semibold tracking-tight">
            Rank title suggestions
          </h1>
          <p className="max-w-3xl text-sm text-muted-foreground">
            Review suggestions based on the prior calendar month&apos;s recorded
            points, stored Guild-role join date, Discord presence, and current
            Beagle rank.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          disabled={busy}
          onClick={() => void query.refetch()}
        >
          <RefreshCw aria-hidden="true" /> Refresh suggestions
        </Button>
      </div>

      {requestMessage ? (
        <StatusAlert tone="warning">{requestMessage}</StatusAlert>
      ) : null}
      {query.isLoading ? (
        <StatusAlert tone="info">Loading rank title suggestions...</StatusAlert>
      ) : query.isError ? (
        <StatusAlert tone="danger">
          Unable to load current rank title suggestions. Refresh before
          selecting members.
        </StatusAlert>
      ) : query.data ? (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Review period</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap items-center justify-between gap-3">
              <p>
                <span className="font-medium">Source:</span> {sourceLabel} ·{" "}
                <span className="font-medium">Evaluated in Berlin:</span>{" "}
                {query.data.evaluatedOn}
              </p>
              <Button
                type="button"
                disabled={busy || selectedIds.length === 0}
                onClick={() => setConfirmOpen(true)}
              >
                Apply selected ({selectedIds.length})
              </Button>
            </CardContent>
          </Card>

          {query.data.rows.length === 0 ? (
            <EmptyState
              icon={<SearchX aria-hidden="true" />}
              title="No members to review"
              description="The current roster has no rank title suggestion rows."
            />
          ) : (
            <Card>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>
                        <Checkbox
                          aria-label="Select all changed suggestions"
                          checked={allSelected}
                          indeterminate={!allSelected && selectedIds.length > 0}
                          disabled={busy || selectableIds.size === 0}
                          onCheckedChange={(checked) =>
                            setSelected(
                              checked ? new Set(selectableIds) : new Set(),
                            )
                          }
                        />
                      </TableHead>
                      <TableHead>Display name</TableHead>
                      <TableHead>Current rank</TableHead>
                      <TableHead>Prior-month points</TableHead>
                      <TableHead>Guild join date</TableHead>
                      <TableHead>Tenure</TableHead>
                      <TableHead>Presence</TableHead>
                      <TableHead>Suggestion/status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {query.data.rows.map((row) => {
                      const selectable =
                        row.eligibility === "eligible" && row.changeRequired
                      return (
                        <TableRow key={row.memberId}>
                          <TableCell>
                            {selectable ? (
                              <Checkbox
                                aria-label={`Select suggestion for ${row.displayName} (${row.memberId})`}
                                checked={selected.has(row.memberId)}
                                disabled={busy}
                                onCheckedChange={(checked) =>
                                  setSelected((current) => {
                                    const next = new Set(current)
                                    if (checked) next.add(row.memberId)
                                    else next.delete(row.memberId)
                                    return next
                                  })
                                }
                              />
                            ) : null}
                          </TableCell>
                          <TableCell className="font-medium">
                            {row.displayName}
                          </TableCell>
                          <TableCell>
                            {row.currentRank
                              ? GUILD_RANK_LABEL[row.currentRank]
                              : "No rank"}
                          </TableCell>
                          <TableCell>
                            {row.points === null ? "Missing" : row.points}
                          </TableCell>
                          <TableCell>
                            {row.joinedAt
                              ? formatGuildJoinDate(row.joinedAt)
                              : "Missing"}
                          </TableCell>
                          <TableCell>
                            {row.tenureDays === null
                              ? "Missing"
                              : `${row.tenureDays} days`}
                          </TableCell>
                          <TableCell>
                            {PRESENCE_LABEL[row.discordPresence]}
                          </TableCell>
                          <TableCell>
                            {suggestionStatus(row, sourceLabel)}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      ) : null}

      {results ? <ApplyResults results={results} /> : null}

      <AlertDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!mutation.isPending) setConfirmOpen(open)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Apply {selectedIds.length} rank title suggestion
              {selectedIds.length === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              The server will recalculate these members from current stored data
              for {sourceLabel}. Only Beagle rank titles may change. Special
              titles, Discord roles, and nicknames remain unchanged.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={mutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={mutation.isPending || selectedIds.length === 0}
              onClick={() => void confirmApply()}
            >
              {mutation.isPending ? "Applying..." : "Confirm apply"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

function ApplyResults({ results }: { results: ApplyTitleSuggestionResult[] }) {
  const applied = results.filter((result) => result.status === "applied").length
  const current = results.filter(
    (result) => result.status === "already-current",
  ).length
  const attention = results.length - applied - current
  return (
    <Card>
      <CardHeader>
        <CardTitle>Last apply results</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-3">
        <p role="status" aria-live="polite">
          {applied} applied, {current} already current, {attention} not applied.
        </p>
        <ul className="grid gap-2">
          {results.map((result) => (
            <li key={result.memberId} className="rounded-xl border p-3">
              <span className="font-medium">
                {result.displayName ?? result.memberId}
              </span>
              : {resultDescription(result)}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}
